import React, { Suspense, useEffect, useRef } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';

import FallbackScreen from '../components/FallbackScreen';
import PrivateRoute from './PrivateRoute';
import { useClinicalSession } from '../contexts/ClinicalSessionContext';
import AssessmentReports from '../pages/AssessmentReports';

// Lazy loaded components
const HomePage = React.lazy(() => import('../pages/Home'));

// Public page lazy imports
const MainPage = React.lazy(() => import('../pages/Updated_UI'));
const Invite = React.lazy(() => import('../pages/Updated_UI/Invite'));
const PrivacyLandingpage = React.lazy(() => import('../pages/Updated_UI/PrivacyLandingpage'));
const TermConditionLandingpage = React.lazy(() => import('../pages/Updated_UI/TermconditionLandingpage'));

// Admin page lazy imports
const AdminHomePage = React.lazy(() => import('../pages/Admin/'));
const AdminTermsPoliclies = React.lazy(() => import('../pages/AdminTermsPolicies/AdminTermsPolicies'));
const EditPolicy = React.lazy(() => import('../pages/AdminTermsPolicies/EditPolicy'));
const TeachersPage = React.lazy(() => import('../pages/Teachers'));
const StudentsPage = React.lazy(() => import('../pages/Students'));
const SchoolAdminPage = React.lazy(() => import('../pages/SchoolsAdmin'));
const AdminTeacher = React.lazy(() => import('../pages/AdminTeacher'));
const AdminStudent = React.lazy(() => import('../pages/AdminStudent'));
const AdminPermissions = React.lazy(() => import('../pages/AdminPermissions'));
const AdminSchools = React.lazy(() => import('../pages/AdminSchools'));
const SchoolPage = React.lazy(() => import('../pages/Schools'));
const OrganizationPage = React.lazy(() => import('../pages/Organization'));

// User page lazy imports
const ProfilePage = React.lazy(() => import('../pages/Profile'));
const PatientOverview = React.lazy(() => import('../pages/PatientOverview'));
const StudentOverview = React.lazy(() => import('../pages/StudentOverview'));
const SelectRole = React.lazy(() => import('../pages/SelectRole'));
const SchoolAdminHome = React.lazy(() => import('../pages/SchoolAdmin'));
const OrganizationAdminDashboad = React.lazy(() => import('../pages/OrganizationAdminDashboad'));

// Content page lazy imports
const ViewAllGoals = React.lazy(() => import('../pages/TeacherDashboard/Goals/viewAllGoals'));
const AllGoals = React.lazy(() => import('../pages/GoalsOverview'));
const DetailGaolGraph = React.lazy(() => import('../pages/GoalsOverview/DetailGaolsGraph'));
const ViewGoal = React.lazy(() => import('../pages/ViewGoal'));
const PendingGoals = React.lazy(() => import('../pages/PendingGoals'));
const AddGoalDetails = React.lazy(() => import('../pages/AddNewGoals/AddGoals'));
const ViewAllVideos = React.lazy(() => import('../pages/Home/ViewAllVideos'));
const PlayVideo = React.lazy(() => import('../pages/PlayVideo'));
const ViewPatientAllVideos = React.lazy(() => import('../pages/ViewPatientAllVideos'));
const BehaviorDashboard = React.lazy(() => import('../pages/QuicktrackDashboard'));
const AddNewBehavior = React.lazy(() => import('../pages/AddNewBehavior'));
const ViewQuickTrack = React.lazy(() => import('../pages/ViewQuickTrack'));
const ProgressReport = React.lazy(() => import('../pages/ProgressReport'));
const TrendAnalysis = React.lazy(() => import('../pages/TrendAnalysis'));

// Settings page lazy imports
const Appointments = React.lazy(() => import('../pages/Appointments'));
const Statistics = React.lazy(() => import('../pages/Statistics'));
const ContactInformation = React.lazy(() => import('../pages/ContactInformation'));
const Message = React.lazy(() => import('../pages/Messaging'));
const UserGuide = React.lazy(() => import('../pages/Userguide'));
const Smartsearch = React.lazy(() => import('../pages/Historical/Smartsearch'));
const Settings = React.lazy(() => import('../pages/Settings'));
const Termcondition = React.lazy(() => import('../pages/Termcondition'));
const Privacy = React.lazy(() => import('../pages/Privacy'));
const Documents = React.lazy(() => import('../pages/Documents'));
const SuperAdminBilling = React.lazy(() => import('../pages/SuperAdminBilling'));
const SessionRouteChangeMinimizer = () => {
  const location = useLocation();
  const { getAllSessions, minimizeSession } = useClinicalSession();
  const hasInitializedRef = useRef(false);
  const getAllSessionsRef = useRef(getAllSessions);

  useEffect(() => {
    getAllSessionsRef.current = getAllSessions;
  }, [getAllSessions]);

  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      return;
    }

    const sessions = getAllSessionsRef.current?.() || [];
    sessions.forEach((session) => {
      if (session.isOpen && !session.isMinimized) {
        minimizeSession(session.id);
      }
    });
  }, [location.key, minimizeSession]);

  return null;
};

const AppRoutes = () => {
  return (
    <Router>
      <Suspense fallback={<FallbackScreen />}>
        <SessionRouteChangeMinimizer />
        <Routes>
          {/* Public Routes */}
          <Route exact path='/main' element={<MainPage />} />
          <Route exact path='/invite/:id' element={<Invite />} />
          <Route
            exact
            path='/privacy-policy'
            element={
              <PrivacyLandingpage
              // footerRequired={true}
              />
            }
          />
          <Route
            exact
            path='/terms-and-conditions'
            element={<TermConditionLandingpage />}
          />

          {/* Home Route */}
          <Route
            exact
            path='/'
            element={
              <PrivateRoute>
                <HomePage />
              </PrivateRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            exact
            path='/admin'
            element={
              <PrivateRoute>
                <AdminHomePage />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/admin/schools'
            element={
              <PrivateRoute>
                <SchoolPage />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/admin/organization'
            element={
              <PrivateRoute>
                <OrganizationPage />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/admin/schoolsAdmin'
            element={
              <PrivateRoute>
                <SchoolAdminPage />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/admin/teachers'
            element={
              <PrivateRoute>
                <TeachersPage />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/admin/students'
            element={
              <PrivateRoute>
                <StudentsPage />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/admin/permissions'
            element={
              <PrivateRoute>
                <AdminPermissions />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/subroles'
            element={
              <PrivateRoute>
                <AdminPermissions />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/admin/terms'
            element={
              <PrivateRoute>
                <AdminTermsPoliclies />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/admin/editPolicies'
            element={
              <PrivateRoute>
                <EditPolicy />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/teachers'
            element={
              <PrivateRoute>
                <AdminTeacher />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/students'
            element={
              <PrivateRoute>
                <AdminStudent />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/schools'
            element={
              <PrivateRoute>
                <AdminSchools />
              </PrivateRoute>
            }
          />

          {/* User Routes */}
          <Route exact path='/termandcondition' element={<ProfilePage />} />
          <Route exact path='/profile' element={<ProfilePage />} />
          <Route
            exact
            path='/selectrole'
            element={
              <PrivateRoute>
                <SelectRole />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/patient/:id'
            element={
              <PrivateRoute>
                <PatientOverview />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/student/:id'
            element={
              <PrivateRoute>
                <StudentOverview />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/schoolAdmin'
            element={
              <PrivateRoute>
                <SchoolAdminHome />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/organizationAdmin'
            element={
              <PrivateRoute>
                <OrganizationAdminDashboad />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/orgschools'
            element={
              <PrivateRoute>
                <SchoolPage />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/organizationTeachers'
            element={
              <PrivateRoute>
                <TeachersPage />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/organizationSchoolAdmins'
            element={
              <PrivateRoute>
                <SchoolAdminPage />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/organizationStudents'
            element={
              <PrivateRoute>
                <StudentsPage />
              </PrivateRoute>
            }
          />

          {/* Content Routes */}
          <Route
            exact
            path='/viewgoals'
            element={
              <PrivateRoute>
                <ViewAllGoals />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/viewallvideos'
            element={
              <PrivateRoute>
                <ViewAllVideos />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/viewallpatientvideos'
            element={
              <PrivateRoute>
                <ViewPatientAllVideos />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/play-video'
            element={
              <PrivateRoute>
                <PlayVideo />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/goalDetails'
            element={
              <PrivateRoute>
                <ViewGoal />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/add-goal'
            element={
              <PrivateRoute>
                <AddGoalDetails />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/pendinggoals'
            element={
              <PrivateRoute>
                <PendingGoals />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/quickTrackView'
            element={
              <PrivateRoute>
                <ViewQuickTrack />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/quicktrackDashboard'
            element={
              <PrivateRoute>
                <BehaviorDashboard />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/addNewBehavior'
            element={
              <PrivateRoute>
                <AddNewBehavior />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/progress-report'
            element={
              <PrivateRoute>
                <ProgressReport />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/assessment-report'
            element={
              <PrivateRoute>
                <AssessmentReports/>
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/trend-analysis'
            element={
              <PrivateRoute>
                <TrendAnalysis />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/allgoals'
            element={
              <PrivateRoute>
                <AllGoals />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/detailed-graph'
            element={
              <PrivateRoute>
                <DetailGaolGraph />
              </PrivateRoute>
            }
          />

          {/* Settings Routes */}
          <Route
            exact
            path='/smartsearch'
            element={
              <PrivateRoute>
                <Smartsearch />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/admin/billing'
            element={
              <PrivateRoute>
                <SuperAdminBilling />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/appointments'
            element={
              <PrivateRoute>
                <Appointments />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/statistics'
            element={
              <PrivateRoute>
                <Statistics />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/historical'
            element={
              <PrivateRoute>
                <Documents />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/message'
            element={
              <PrivateRoute>
                <Message />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/settings'
            element={
              <PrivateRoute>
                <Settings />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/Termcondition'
            element={
              <PrivateRoute>
                <Termcondition />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/Privacy'
            element={
              <PrivateRoute>
                <Privacy />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/Contact'
            element={
              <PrivateRoute>
                <ContactInformation />
              </PrivateRoute>
            }
          />
          <Route
            exact
            path='/Userguide'
            element={
              <PrivateRoute>
                <UserGuide />
              </PrivateRoute>
            }
          />

          {/* Fallback route */}
          <Route path='/*' element={<Navigate to='/' replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default AppRoutes;